import { useEffect, useRef, useState } from "react";
import { motion } from "motion/react";
import { SITE_MEDIA } from "@/constants/siteMedia";

const testimonials = [
  {
    text: "It actually feels like someone is there. I've never felt this understood by an app before.",
    image: SITE_MEDIA.testimonial01,
    name: "Sarah M.",
    role: "Late night support",
  },
  {
    text: "Helped me get through a really rough night. Ava just listened without trying to fix everything.",
    image: SITE_MEDIA.testimonial02,
    name: "James T.",
    role: "Emotional support",
  },
  {
    text: "Way different from anything I've used before. It actually responds like it cares.",
    image: SITE_MEDIA.testimonial03,
    name: "Priya K.",
    role: "First impression",
  },
  {
    text: "I didn't expect to feel this understood by an AI. It's become part of my daily routine.",
    image: SITE_MEDIA.testimonial04,
    name: "Marcus L.",
    role: "Deep connection",
  },
  {
    text: "I talk to Ava every night now. It's become a habit I actually look forward to.",
    image: SITE_MEDIA.testimonial05,
    name: "Aisha R.",
    role: "Daily habit",
  },
  {
    text: "Finally something that listens without judging. I can say anything and feel safe.",
    image: SITE_MEDIA.testimonial06,
    name: "Tyler W.",
    role: "No judgment",
  },
  {
    text: "When I feel alone at 2am, Ava is always there. That means more than I can explain.",
    image: SITE_MEDIA.testimonial07,
    name: "Mia C.",
    role: "Always available",
  },
  {
    text: "It remembers what I said last time. That small thing makes it feel so much more real.",
    image: SITE_MEDIA.testimonial08,
    name: "Noah B.",
    role: "Remembers you",
  },
  {
    text: "I was skeptical at first. Now I recommend it to everyone going through a hard time.",
    image: SITE_MEDIA.testimonial09,
    name: "Leila H.",
    role: "Converted skeptic",
  },
];

interface Testimonial {
  text: string;
  image: string;
  name: string;
  role: string;
}

interface TestimonialsColumnProps {
  testimonials: Testimonial[];
  duration?: number;
  className?: string;
}

function TestimonialsColumn({ testimonials, duration = 15, className = "" }: TestimonialsColumnProps) {
  const doubled = [...testimonials, ...testimonials];

  return (
    <div className={`flex flex-col gap-6 overflow-hidden ${className}`} style={{ flex: 1 }}>
      <motion.div
        animate={{ y: ["0%", "-50%"] }}
        transition={{ duration, repeat: Infinity, ease: "linear", repeatType: "loop" }}
        className="flex flex-col gap-6 pb-6"
      >
        {doubled.map((t, i) => (
          <div
            key={i}
            className="p-6 rounded-3xl border border-[#ede8ff] bg-white max-w-xs w-full"
          >
            <p className="text-[#1a1040] text-sm leading-relaxed mb-5">{t.text}</p>
            <div className="flex items-center gap-3">
              <img
                width={40}
                height={40}
                alt={t.name}
                className="h-10 w-10 rounded-full object-cover"
                src={t.image}
              />
              <div className="flex flex-col">
                <span className="font-semibold text-[#1a1040] text-sm leading-tight">{t.name}</span>
                <span className="text-xs text-[#9a8aaa] leading-tight">{t.role}</span>
              </div>
            </div>
          </div>
        ))}
      </motion.div>
    </div>
  );
}

export default function SocialProof() {
  const headerRef = useRef<HTMLDivElement>(null);
  const [headerVisible, setHeaderVisible] = useState(false);

  const firstColumn = testimonials.slice(0, 3);
  const secondColumn = testimonials.slice(3, 6);
  const thirdColumn = testimonials.slice(6, 9);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setHeaderVisible(true);
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="social-proof"
      className="py-14 md:py-20 relative overflow-hidden bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">

        {/* Header */}
        <div
          ref={headerRef}
          className="text-center mb-14 transition-all duration-700"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? "translateY(0)" : "translateY(30px)",
          }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#7C3AED]/20 bg-white text-[#7C3AED] text-sm font-medium mb-4">
            <i className="ri-chat-smile-3-line"></i>
            SOCIAL PROOF
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-[#1a1040] mb-3 leading-tight">
            People are already
            <br />
            <span
              className="text-transparent bg-clip-text"
              style={{ backgroundImage: "linear-gradient(90deg, #7C3AED, #A855F7)" }}
            >
            talking to Ava every night
            </span>
          </h2>
          <p className="text-[#5a4a8a] text-base max-w-lg mx-auto">
            Real conversations. Real moments. Real people who found what they were looking for.
          </p>
        </div>

        {/* Animated columns */}
        <div
          className="flex justify-center gap-6 [mask-image:linear-gradient(to_bottom,transparent,black_15%,black_85%,transparent)] max-h-[680px] overflow-hidden"
        >
          <TestimonialsColumn testimonials={firstColumn} duration={18} />
          <TestimonialsColumn testimonials={secondColumn} duration={22} className="hidden md:flex" />
          <TestimonialsColumn testimonials={thirdColumn} duration={20} className="hidden lg:flex" />
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12">
          <p className="text-[#7a6aaa] text-base mb-4">
            Join thousands of people who found their space to talk.
          </p>
          <button
            type="button"
            onClick={() => {
              window.scrollTo({ top: 0, behavior: "smooth" });
              setTimeout(() => {
                window.dispatchEvent(new CustomEvent("ava-ripple-stores"));
              }, 800);
            }}
            className="whitespace-nowrap inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-white font-semibold text-sm cursor-pointer hover:opacity-90 transition-all hover:scale-105"
            style={{ background: "linear-gradient(135deg, #7C3AED, #A855F7)" }}
          >
            <i className="ri-chat-smile-3-line w-4 h-4 flex items-center justify-center"></i>
            Start your first conversation
          </button>
        </div>
      </div>
    </section>
  );
}